import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import ReportExportButtons from "../../components/ReportExportButtons";
import { useReportData } from "../../hooks/useReportData";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type CertificatePipelineResponse,
  type CertificatePipelineItem,
  type CertificatePipelineParams,
} from "../../services/reports-extended.api";
import { fetchDepartments } from "@/features/announcements/services/departments.api";
import { Approve, Recent, Shield } from "@/components/icons";
import styles from "./CertificatePipelineReportPage.module.scss";

const PAGE_SIZE = 20;

const CertificatePipelineReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [statusFilter, setStatusFilter] = useState<
    "ALL" | "ISSUED" | "PENDING"
  >("ALL");
  const [page, setPage] = useState(1);
  // ReportFilterBar `departments` prop'u verilmediğinde yalnızca "Tüm Birimler"
  // seçeneği dönüyordu. ReportsPage'de olduğu gibi birimleri fetch edip
  // dropdown'ı dolduruyoruz — aynı limit=100 limit'i (pagination'lı birim
  // listeleri için yeterli).
  const [departments, setDepartments] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    fetchDepartments({ limit: 100 })
      .then((list) => {
        if (!cancelled) {
          setDepartments(list.map((d) => ({ id: d.id, name: d.name })));
        }
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const params: CertificatePipelineParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      departmentID: filter.departmentID,
      search: filter.search?.trim() || undefined,
      status: statusFilter,
    }),
    [filter, statusFilter, page],
  );

  const { data, isLoading, error } = useReportData<
    CertificatePipelineResponse,
    CertificatePipelineParams
  >({
    fetcher: (p, signal) => reportsExtendedApi.getCertificatePipeline(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;
  const buckets = data?.buckets ?? [];

  const columns: TableColumn<CertificatePipelineItem>[] = [
    {
      key: "userFullName",
      label: "AD SOYAD",
      header: "AD SOYAD",
      accessor: (row) => row.userFullName,
    },
    {
      key: "department",
      label: t("reports.certificatePipeline.headerDepartment"),
      header: t("reports.certificatePipeline.headerDepartment"),
      accessor: (row) => row.department ?? "—",
    },
    {
      key: "educationTitle",
      label: t("reports.certificatePipeline.headerTraining"),
      header: t("reports.certificatePipeline.headerTraining"),
      accessor: (row) => row.educationTitle,
    },
    {
      key: "completedAt",
      label: "TAMAMLAMA",
      header: "TAMAMLAMA",
      accessor: (row) =>
        row.completedAt
          ? new Date(row.completedAt).toLocaleDateString("tr-TR")
          : "—",
    },
    {
      key: "issuedAt",
      label: t("reports.certificatePipeline.headerIssued"),
      header: t("reports.certificatePipeline.headerIssued"),
      accessor: (row) =>
        row.issuedAt
          ? new Date(row.issuedAt).toLocaleDateString("tr-TR")
          : "—",
    },
    {
      key: "ageDays",
      label: t("reports.certificatePipeline.headerAge"),
      header: t("reports.certificatePipeline.headerAge"),
      accessor: (row) =>
        row.ageDays !== null ? (
          <span className={row.isBacklogged ? styles.agedBadge : undefined}>
            {row.ageDays}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "status",
      label: "DURUM",
      header: "DURUM",
      accessor: (row) => (
        <span
          className={`${styles.statusBadge} ${row.status === "ISSUED" ? styles.issued : styles.pending}`}
        >
          {row.status === "ISSUED" ? "Verildi" : "Bekliyor"}
        </span>
      ),
    },
  ];

  // R11 yapılandırılabilirliği: kolon ayarları, kayıtlı filtre setleri,
  // zamanlanmış e-posta raporu. Blok düzenini ReportDetailLayout yönetir.
  const cfg = useReportConfig<CertificatePipelineItem>({
    reportKey: "CERTIFICATE_PIPELINE",
    reportTitle: "Sertifika Pipeline",
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title="Sertifika Pipeline"
      layoutKey={`report:CERTIFICATE_PIPELINE`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.operational.title")}
      description={t("reports.certificatePipeline.description")}
      exportSlot={
        <>
          {cfg.toolbar}
          <ReportExportButtons
          reportType="CERTIFICATE_PIPELINE"
          filters={params as unknown as Record<string, unknown>}
          filenameBase="sertifika-pipeline"
          exportFn={reportsExtendedApi.exportReport}
          disabled={isLoading}
        />
        </>
      }
      filterSlot={
        <ReportFilterBar
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          departments={departments}
          extras={
            <div className={styles.statusFilterGroup}>
              {(["ALL", "PENDING", "ISSUED"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.statusFilterButton} ${statusFilter === s ? styles.statusFilterActive : ""}`}
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                >
                  {s === "ALL" ? t("reports.certificatePipeline.all") : s === "PENDING" ? t("reports.certificatePipeline.pending") : t("reports.certificatePipeline.issued")}
                </button>
              ))}
            </div>
          }
        />
      }
    >
      <div className={styles.kpiGrid}>
        <ReportKPICard
          title="Toplam (Tamamlanan)"
          value={summary?.total ?? 0}
          icon={<Approve width={18} height={18} />}
        />
        <ReportKPICard
          title="Verilen Sertifika"
          value={summary?.issued ?? 0}
          icon={<Approve width={18} height={18} />}
          accent="success"
          footnote={
            summary ? `%${summary.issueRate.toFixed(1)} oran` : undefined
          }
        />
        <ReportKPICard
          title="Bekleyen"
          value={summary?.pending ?? 0}
          icon={<Recent width={18} height={18} />}
          accent="warning"
        />
        <ReportKPICard
          title={t("reports.certificatePipeline.backlog30")}
          value={summary?.backlogOver30Days ?? 0}
          icon={<Shield width={18} height={18} />}
          accent="danger"
          footnote={
            summary ? `Ort. ${summary.avgIssueDays} gün veriliş` : undefined
          }
        />
      </div>

      <ReportChart
        title={t("reports.certificatePipeline.monthlyMovements")}
        subtitle="Verilen vs Bekleyen (son 12 ay)"
        type="bar"
        isLoading={isLoading}
        isEmpty={buckets.length === 0}
        series={[
          {
            name: "Verildi",
            data: buckets.map((b) => b.issued),
          },
          {
            name: "Bekliyor",
            data: buckets.map((b) => b.pending),
          },
        ]}
        options={{
          xaxis: { categories: buckets.map((b) => b.month) },
          colors: ["#16a34a", "#f59e0b"],
          plotOptions: {
            bar: { horizontal: false, columnWidth: "50%", borderRadius: 4 },
          },
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.enrollmentID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error ? t("reports.certificatePipeline.noData") : t("reports.certificatePipeline.noCertificate")
        }
      />
    </ReportDetailLayout>
  );
};

export default CertificatePipelineReportPage;
